import { Alert, Box, Card, Stack, Tab, Tabs } from '@mui/material';
import React from 'react';
import { Breadcrumb } from 'src/components/Breadcrumb';
import PrivateLayoutView from 'src/Layout/PrivateLayout/PrivateLayoutView';
import { ContactCaptureActionBar } from './ContactCaptureActionBar';
import { ContactCaptureBuilderProvider, useBuilder } from './ContactCaptureBuilderContext';
import { ContactCaptureContentTab } from './ContactCaptureContentTab';
import { ContactCaptureDesignTab } from './ContactCaptureDesignTab';
import { ContactCaptureInstallTab } from './ContactCaptureInstallTab';
import { ContactCaptureLoadingState } from './ContactCaptureLoadingState';
import { ContactCapturePreview } from './ContactCapturePreview';
import { DeleteContactCaptureDialog } from './DeleteContactCaptureDialog';
import { BuilderTab, useContactCaptureBuilder } from './useContactCaptureBuilder';

const BuilderTabs = () => {
  const { setTab, tab } = useBuilder();

  return (
    <Card variant="outlined" sx={{ minHeight: 0, overflow: 'hidden' }}>
      <Stack sx={{ height: '100%', minHeight: 0 }}>
        <Box px={2} borderBottom={1} borderColor="divider">
          <Tabs
            value={tab}
            onChange={(_, value: BuilderTab) => setTab(value)}
            aria-label="Contact capture builder sections"
          >
            <Tab value="content" label="Content" />
            <Tab value="design" label="Design" />
            <Tab value="install" label="Install" />
          </Tabs>
        </Box>
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: { xs: 2, md: 3 } }}>
          {tab === 'content' && <ContactCaptureContentTab />}
          {tab === 'design' && <ContactCaptureDesignTab />}
          {tab === 'install' && <ContactCaptureInstallTab />}
        </Box>
      </Stack>
    </Card>
  );
};

const BuilderContent = () => {
  const { consentText, controller, publishing, settings, type } = useBuilder();

  return (
    <Stack sx={{ height: '100%', minHeight: 0 }} spacing={2}>
      {!publishing && (controller.record?.status === 'draft' || controller.record?.hasUnpublishedChanges) && (
        <Alert severity="warning">
          This widget has unpublished changes. Click Publish to apply the latest version to the live widget.
        </Alert>
      )}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(520px, 1.5fr) minmax(360px, 1fr)' },
          gap: 2,
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
        }}
      >
        <BuilderTabs />
        <Card variant="outlined" sx={{ minHeight: { lg: 0 }, overflow: 'hidden', display: 'flex' }}>
          <ContactCapturePreview settings={settings} type={type} consentText={consentText} />
        </Card>
      </Box>
      <ContactCaptureActionBar />
    </Stack>
  );
};

export const ContactCaptureBuilderView = () => {
  const builder = useContactCaptureBuilder();
  const title = builder.id ? 'Edit contact capture' : 'Create contact capture';
  const breadcrumbs = [
    { label: 'Contact Capture', path: '/contact-capture' },
    { label: title, isActive: true },
  ];

  if (builder.id && (
    builder.controller.loading || builder.controller.error || !builder.controller.record
  )) {
    return (
      <PrivateLayoutView title={<Breadcrumb items={breadcrumbs} />}>
        <ContactCaptureLoadingState controller={builder.controller} />
      </PrivateLayoutView>
    );
  }

  return (
    <ContactCaptureBuilderProvider builder={builder}>
      <PrivateLayoutView title={<Breadcrumb items={breadcrumbs} />}>
        <BuilderContent />
      </PrivateLayoutView>
      <DeleteContactCaptureDialog />
    </ContactCaptureBuilderProvider>
  );
};

export default ContactCaptureBuilderView;
